/**
 * FieldExamView -- the SAMA field-examiner's questions, answered live (P336).
 *
 * Route: /twin/field-exam. Grounded in real supervisory findings
 * (docs/sama-field-exam-gap-analysis.md): the examiner's method tests
 * DISCRIMINATION (classifications must discriminate -- the "0 of 313 core"
 * and "46 of 46 critical" poles), COHERENCE (criticality must reconcile
 * across the joins), and per-component TEST COVERAGE + impact-to-channel.
 * This view runs the same four tests over the live estate, with named rows
 * so every flag is one click from its fix.
 *
 * Hardcoded English to match the sibling exec views.
 */
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import type { SupervisoryCoherenceFlag } from "shared/api/types";
import { MarsadIcon } from "shared/components/MarsadIcon";
import { flagDetailPath } from "shared/routes/entityRoutes";
import { useSupervisoryChecks } from "twin/fieldexam/hooks";
import { canRunOperations } from "shared/api/authority";
import { useMe } from "shared/api/useMe";

import { Bdi } from "shared/components/Bdi";
import { Figure } from "shared/components/Figure";
import { checkDetail, checkLabel } from "shared/supervisory/checkWords";
/**
 * Round 12 B1 -- three states, because a check that could not run has
 * not passed.
 *
 * The field exam awarded an empty bank seven OK ticks out of eleven,
 * each reasoned "register empty (population is checked elsewhere)". The
 * explanation is honest and the word beside it was not, and of the five
 * surfaces that painted green over an unassessed estate this is the one
 * that turned it into a supervisory pass.
 */
/** B6 -- the names that appear more than once in a member list. */
function repeatedNames(members: { name: string }[]): Set<string> {
  const seen = new Map<string, number>();
  for (const m of members) seen.set(m.name, (seen.get(m.name) ?? 0) + 1);
  return new Set([...seen.entries()].filter(([, n]) => n > 1).map(([name]) => name));
}

function Pill({ ok, assessed }: { ok: boolean; assessed: boolean }): JSX.Element {
  const { t } = useTranslation();
  const tone = !assessed
    ? "bg-slate-100 text-slate-700 ring-slate-300"
    : ok
      ? "bg-emerald-100 text-emerald-800 ring-emerald-300"
      : "bg-rose-100 text-rose-800 ring-rose-300";
  return (
    <span
      data-testid="field-exam-check-pill"
      data-verdict={!assessed ? "not-assessed" : ok ? "ok" : "flagged"}
      className={`rounded px-2 py-0.5 text-xs font-semibold ring-1 ${tone}`}
    >
      {!assessed
        ? t("screens.fieldExamView.notAssessed")
        : ok
          ? t("screens.fieldExamView.ok")
          : t("screens.fieldExamView.flagged")}
    </span>
  );
}

function CoherenceGroup({
  label,
  flags,
}: {
  label: string;
  flags: SupervisoryCoherenceFlag[];
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="rounded border border-rose-200 bg-rose-50/50 p-3">
      <p className="text-sm font-medium text-rose-900">
        {flags[0] ? checkLabel(t, flags[0]) : label}{" "}
        <span className="text-xs font-normal text-rose-700">({flags.length})</span>
      </p>
      <p className="mt-0.5 text-xs text-slate-500">
        <Figure>{flags[0] ? checkDetail(t, flags[0]) : ""}</Figure>
      </p>
      {/* D4 -- one chip per FLAG named one row per standard, because
          the backend sends one flag per standard rather than one per
          breach (deliberately -- per-breach flooded the list from 28 to
          66). A flag that counts thirty rows can name them, so the ones
          it counted are chips too. */}
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {flags.flatMap((f) =>
          (f.members ?? []).map((m) => {
            const memberTo = flagDetailPath(m.kind, m.id);
            const cls = "rounded bg-white px-1.5 py-0.5 text-xs ring-1 ring-rose-200";
            return memberTo ? (
              <Link
                key={`${f.key}:${m.id}`}
                to={memberTo}
                data-testid={`field-exam-flag-member-${m.id}`}
                className={`${cls} text-brand-brassDark hover:underline`}
              >
                <Bdi>{m.name}</Bdi>
              </Link>
            ) : (
              <span
                key={`${f.key}:${m.id}`}
                data-testid={`field-exam-flag-member-${m.id}`}
                className={`${cls} text-slate-700`}
              >
                <Bdi>{m.name}</Bdi>
              </span>
            );
          }),
        )}
        {flags.some((f) => f.truncated) && (
          <span className="px-1 text-xs text-slate-500">
            {/* Stated rather than dangled: the reader knows the list is
                partial and that the standards surface holds all of it. */}
            <Link
              to="/twin/architecture-standards"
              data-testid="field-exam-flag-more"
              className="text-brand-brassDark hover:underline"
            >
              {/* Round 19 I23 -- it printed the server's English label. */}
              {flags[0] ? checkLabel(t, flags[0]) : label}
            </Link>
          </span>
        )}
        {flags.every((f) => !(f.members ?? []).length) &&
          flags.map((f) => {
            // D3 -- the check names the row it flagged. Sending the
            // reader to look for it again is the product withholding an
            // answer it already has.
            const to = flagDetailPath(f.entity_kind, f.entity_id);
            const className =
              "rounded bg-white px-1.5 py-0.5 text-xs ring-1 ring-rose-200";
            return to ? (
              <Link
                key={f.entity_id}
                to={to}
                data-testid={`field-exam-flag-${f.entity_id}`}
                className={`${className} text-brand-brassDark hover:underline`}
              >
                {f.entity_name}
              </Link>
            ) : (
              <span
                key={f.entity_id}
                data-testid={`field-exam-flag-${f.entity_id}`}
                className={`${className} text-slate-700`}
              >
                {f.entity_name}
              </span>
            );
          })}
      </div>
    </div>
  );
}

function Bool({ v, neutral = false }: { v: boolean; neutral?: boolean }): JSX.Element {
  /* Round 19 B30 -- a fact a worker computed (a blast radius sealed) is
     not a judgement, so it is not green or red. */
  if (neutral) {
    return (
      <span data-neutral="true" className="text-slate-500">
        {v ? "✓" : "–"}
      </span>
    );
  }
  return v ? (
    <span className="text-emerald-700">✓</span>
  ) : (
    <span className="text-rose-700">✗</span>
  );
}

export function FieldExamView(): JSX.Element {
  const { t } = useTranslation();
  /* H -- whose instrument this is. The resilience lead runs it; a
     service owner has the tab and nothing on the page to do. */
  const me = useMe();
  const isOperator = canRunOperations(me.data);
  const { data, isLoading, isError } = useSupervisoryChecks();

  // B6 -- the figures the header prints are the rows the sections list.

  const distFlagged = (data?.distribution ?? []).filter(
    (c) => !c.ok && c.assessed !== false,
  ).length;

  const distNotAssessed = (data?.distribution ?? []).filter(
    (c) => c.assessed === false,
  ).length;

  // B8 -- "no contradictions" is only an all-clear when something was

  // there to contradict. On empty registers nothing was reconciled.

  const nothingToReconcile =
    data?.coherence_assessed === false ||
    ((data?.distribution ?? []).length > 0 &&
      (data?.distribution ?? []).every((c) => c.assessed === false));

  const coherenceGroups = new Map<string, SupervisoryCoherenceFlag[]>();
  for (const f of data?.coherence ?? []) {
    const list = coherenceGroups.get(f.label) ?? [];
    list.push(f);
    coherenceGroups.set(f.label, list);
  }

  return (
    <article data-testid="field-exam-view" className="space-y-5">
      <header className="space-y-1">
        <h1 className="font-display text-2xl text-slate-900">
          <MarsadIcon
            name="verifier"
            className="me-2 inline-block h-6 w-6 align-[-4px]"
          />
          {t("screens.fieldExamView.fieldExamReadiness")}
        </h1>
        <p className="max-w-3xl text-sm text-slate-500">
          {t("screens.fieldExamView.theQuestionsASamaOperationalResilienceField")}{" "}
          <span className="font-medium">{t("screens.fieldExamView.discriminate")}</span>
          {t("screens.fieldExamView.doThey")}{" "}
          <span className="font-medium">{t("screens.fieldExamView.cohere")}</span>{" "}
          {t("screens.fieldExamView.acrossLayersAndIsEveryCriticalComponent")}{" "}
          <span className="font-medium">{t("screens.fieldExamView.tested")}</span>{" "}
          {t("screens.fieldExamView.withItsImpactOnDeliveryChannelsAssessed")}
        </p>
        {/* ROUND 13 H, thirteenth round -- whose instrument this is.

            The tab is in the Service Owner's navigation and the page
            carries no control he can use: thirty-nine hostnames, a
            grid of checks, and nothing asked of him. The names are
            right -- they are the bank's own, out of its own CMDB --
            and the page is a read surface by design. What was missing
            was the sentence saying so, and what on it is his. */}
        {!isOperator && (
          <p
            data-testid="field-exam-audience"
            className="max-w-3xl text-xs text-slate-600"
          >
            {t("screens.fieldExamView.whoseInstrument")}{" "}
            <Link
              to="/twin/ibs-portfolio"
              data-testid="field-exam-audience-yours"
              className="font-medium text-brand-brassDark hover:underline"
            >
              {t("screens.fieldExamView.whoseInstrumentYours")}
            </Link>
          </p>
        )}
      </header>

      {isLoading ? (
        /* Round 12 F7 -- this page sat on a bare "Loading..." for 16.2
           seconds from navigation to the first figure on one seat and
           eight on another, with no skeleton and no shape. A reader who
           cannot see what is coming cannot tell a slow page from a
           broken one, and this page's own answer takes as long as it
           takes: the checks run over the whole estate.

           The skeleton is the page's real shape, so what arrives lands
           where the grey blocks were rather than reflowing the screen
           under a reader who has started to look. */
        <div data-testid="field-exam-loading" className="space-y-4">
          <p className="text-sm text-slate-500">{t("screens.fieldExamView.loading")}</p>
          <div
            aria-hidden="true"
            className="rounded-xl border border-paper-2 bg-white p-4 shadow-card"
          >
            <div className="h-8 w-24 animate-pulse rounded bg-slate-100" />
            <div className="mt-2 h-4 w-64 animate-pulse rounded bg-slate-100" />
          </div>
          <div aria-hidden="true" className="space-y-2">
            {[0, 1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="flex items-center gap-3 rounded border border-slate-200 bg-white px-3 py-2"
              >
                <div className="h-4 flex-1 animate-pulse rounded bg-slate-100" />
                <div className="h-4 w-16 animate-pulse rounded bg-slate-100" />
              </div>
            ))}
          </div>
        </div>
      ) : isError || !data ? (
        <div data-testid="field-exam-error" className="text-sm text-slate-500">
          {t("screens.fieldExamView.checksUnavailable")}
        </div>
      ) : (
        <>
          <section
            data-testid="field-exam-summary"
            className="rounded-xl border border-paper-2 bg-white p-4 shadow-card"
          >
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              {/* ROUND 15 B6 -- criterion 4. The header read "6 of 13
                  flagged · 9 could not be assessed" over four flagged
                  rows and nine not-assessed ones, because it summed
                  three populations (distribution checks, coherence
                  flags, the coverage question) into one figure and the
                  page lists them as three sections. The header is the
                  sum of the rows now, per section, so a reader can
                  count them and get the same number. */}
              <span className="font-display text-3xl text-slate-900 tabular-nums">
                {distFlagged}
              </span>
              <span
                className="text-sm text-slate-600"
                data-testid="field-exam-dist-summary"
              >
                {t("screens.fieldExamView.ofDistributionChecksFlagged", {
                  total: data.distribution.length,
                })}
              </span>
              {/* B1 -- "11 checks, 4 flagged" invites a reader to
                  subtract to seven passes that never happened. What
                  could not run says so beside the count. */}
              {distNotAssessed > 0 && (
                <span
                  data-testid="field-exam-not-assessed"
                  className="text-sm text-slate-600"
                >
                  {t("screens.fieldExamView.notAssessedCount", {
                    count: distNotAssessed,
                  })}
                </span>
              )}
              <span
                data-testid="field-exam-coherence-summary"
                className="text-sm text-slate-600"
              >
                {t("screens.fieldExamView.coherenceFlagsCount", {
                  count: data.coherence.length,
                })}
              </span>
              {data.coverage_pct !== null && (
                <span
                  data-testid="field-exam-coverage"
                  className="text-sm text-slate-600 tabular-nums"
                >
                  {/* Round 14 D2 -- the run-based figure, P419's, with
                      the counts behind it. It was services with a
                      scenario DEFINED or a drill passed, and read 100
                      per cent beside "0 of 5 operations tested". */}
                  {t("screens.fieldExamView.testedCoverage", {
                    pct: data.coverage_pct,
                    tested: data.coverage_tested ?? 0,
                    required: data.coverage_required ?? 0,
                  })}{" "}
                  <Link
                    to="/twin/test-runs"
                    className="text-brand-brassDark underline decoration-dotted underline-offset-2"
                  >
                    {t("screens.fieldExamView.testedCoverageLink")}
                  </Link>
                </span>
              )}
            </div>
          </section>

          <section className="space-y-2">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
              {t("screens.fieldExamView.classificationDiscrimination")}
            </h2>
            {data.distribution.map((c) => (
              <div
                key={c.key}
                data-testid={`field-exam-dist-${c.key}`}
                className="flex items-start justify-between gap-3 rounded border border-paper-2 bg-white px-3 py-2"
              >
                <div>
                  <p className="text-sm text-slate-800">{checkLabel(t, c)}</p>
                  <p className="text-xs text-slate-500">
                    {/* C1 -- a supervisory check's detail is composed by
                        the server out of counts and OUR OWN vocabulary
                        ("3 tier_0 / 2 tier_1"), not prose a translator
                        writes. Two figures with a spaced separator
                        between them swap in an RTL paragraph, so the
                        examiner's own summary reported the wrong counts
                        against the wrong classes. */}
                    <Figure>{checkDetail(t, c)}</Figure>
                  </p>
                  {/* D10 -- the rows the check counted, openable.
                      The readiness check NAMED a failing connector and
                      the reader could not open it, on a page whose own
                      strapline says every flag names its rows. The
                      examiner's workspace has shown these since CX6;
                      this surface was simply served less. */}
                  {(c.classes ?? []).some((cl) => (cl.members ?? []).length > 0) && (
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {(c.classes ?? []).flatMap((cl) =>
                        (cl.members ?? []).map((m) => {
                          const to = flagDetailPath(m.kind, m.id);
                          // B6 -- two records with one name read as one
                          // record listed twice; the code tells them apart.
                          const label =
                            repeatedNames(
                              (c.classes ?? []).flatMap((x) => x.members ?? []),
                            ).has(m.name) && m.code
                              ? `${m.name} (${m.code})`
                              : m.name;
                          const cls =
                            "rounded bg-paper px-1.5 py-0.5 text-[11px] ring-1 ring-paper-2";
                          return to ? (
                            <Link
                              key={`${cl.value}:${m.id}`}
                              to={to}
                              data-testid={`field-exam-class-member-${m.id}`}
                              className={`${cls} text-brand-brassDark hover:underline`}
                            >
                              <Bdi>{label}</Bdi>
                            </Link>
                          ) : (
                            <span
                              key={`${cl.value}:${m.id}`}
                              data-testid={`field-exam-class-member-${m.id}`}
                              className={`${cls} text-slate-600`}
                            >
                              <Bdi>{label}</Bdi>
                            </span>
                          );
                        }),
                      )}
                    </div>
                  )}
                </div>
                <Pill ok={c.ok} assessed={c.assessed !== false} />
              </div>
            ))}
          </section>

          <section className="space-y-2">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
              {t("screens.fieldExamView.crossLayerCoherence")}
            </h2>
            {coherenceGroups.size === 0 ? (
              nothingToReconcile ? (
                <p
                  data-testid="field-exam-nothing-to-reconcile"
                  className="text-sm text-slate-600"
                >
                  {t("screens.fieldExamView.nothingToReconcileYet")}
                </p>
              ) : (
                <p
                  data-testid="field-exam-coherent"
                  className="text-sm text-emerald-700"
                >
                  {t(
                    "screens.fieldExamView.noCoherenceContradictionsClassificationsReco",
                  )}
                </p>
              )
            ) : (
              [...coherenceGroups.entries()].map(([label, flags]) => (
                <CoherenceGroup key={label} label={label} flags={flags} />
              ))
            )}
          </section>

          <section className="space-y-2">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
              {t("screens.fieldExamView.criticalComponentTestCoverage2")}
            </h2>
            <div className="overflow-x-auto rounded border border-paper-2 bg-white">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-start text-xs uppercase tracking-wide text-slate-400">
                    <th className="px-3 py-2 text-start">
                      {t("screens.fieldExamView.criticalService")}
                    </th>
                    <th className="px-3 py-2">
                      {t("screens.fieldExamView.scenarioDefined")}
                    </th>
                    <th className="px-3 py-2">{t("screens.fieldExamView.drill")}</th>
                    <th className="px-3 py-2">
                      {t("screens.fieldExamView.blastSealed")}
                    </th>
                    <th className="px-3 py-2">
                      {t("screens.fieldExamView.channelsReached")}
                    </th>
                    <th className="px-3 py-2">
                      {t("screens.fieldExamView.ciGrainTargets")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.coverage.map((r) => (
                    <tr
                      key={r.service_id}
                      data-testid={`field-exam-cov-${r.service_id}`}
                      className={r.covered ? "" : "bg-rose-50/50"}
                    >
                      <td className="px-3 py-1.5 text-slate-800">{r.service_name}</td>
                      <td className="px-3 py-1.5 text-center">
                        <Bool v={r.scenario_assessed} />
                      </td>
                      <td className="px-3 py-1.5 text-center">
                        <Bool v={r.drill_covered} />
                      </td>
                      <td className="px-3 py-1.5 text-center">
                        <Bool v={r.blast_sealed} neutral />
                      </td>
                      <td className="px-3 py-1.5 text-center tabular-nums">
                        {r.channels_reached}
                      </td>
                      <td
                        className={`px-3 py-1.5 text-center tabular-nums ${
                          r.ci_scenario_targets > 0
                            ? "text-emerald-700"
                            : "text-slate-400"
                        }`}
                      >
                        {r.ci_scenario_targets}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-slate-400">
              {t("screens.fieldExamView.ciGrainTargetsThisServiceSTechnology")}
            </p>
          </section>
        </>
      )}
    </article>
  );
}
